import type { CmsPagesExportFile } from "../../../types/cms";

export const NOT_AN_EXPORT_FILE_MESSAGE =
  "This isn't a CMS pages export file. Choose a .json file downloaded from a page's Export section.";

export const UNSUPPORTED_EXPORT_VERSION_MESSAGE =
  "This export file comes from a different version of the CMS and can't be imported here.";

const EXPORT_FORMAT = "legal500-cms-pages";

const EXPORT_VERSION = 2;

const SUPPORTED_VERSIONS: unknown[] = [1, EXPORT_VERSION];

const IMAGE_EXTENSIONS: unknown[] = ["png", "jpg", "gif", "webp"];

const IMAGE_HASH = /^[0-9a-f]{64}$/;

export type ParsedExportFile =
  | { exportFile: CmsPagesExportFile; error: null }
  | { exportFile: null; error: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isExportFilePage(value: unknown): boolean {
  return isRecord(value) && typeof value.slug === "string" && typeof value.title === "string";
}

function isExportFileImage(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.hash === "string" &&
    IMAGE_HASH.test(value.hash) &&
    IMAGE_EXTENSIONS.includes(value.ext) &&
    typeof value.contentType === "string" &&
    Number.isInteger(value.size) &&
    typeof value.data === "string"
  );
}

export function parsePagesExportFile(text: string): ParsedExportFile {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { exportFile: null, error: NOT_AN_EXPORT_FILE_MESSAGE };
  }
  if (!isRecord(parsed) || parsed.format !== EXPORT_FORMAT) {
    return { exportFile: null, error: NOT_AN_EXPORT_FILE_MESSAGE };
  }
  if (!SUPPORTED_VERSIONS.includes(parsed.version)) {
    return { exportFile: null, error: UNSUPPORTED_EXPORT_VERSION_MESSAGE };
  }
  if (!Array.isArray(parsed.pages) || parsed.pages.length === 0) {
    return { exportFile: null, error: NOT_AN_EXPORT_FILE_MESSAGE };
  }
  if (!parsed.pages.every(isExportFilePage)) {
    return { exportFile: null, error: NOT_AN_EXPORT_FILE_MESSAGE };
  }
  if (
    parsed.version === EXPORT_VERSION &&
    (!Array.isArray(parsed.images) || !parsed.images.every(isExportFileImage))
  ) {
    return { exportFile: null, error: NOT_AN_EXPORT_FILE_MESSAGE };
  }
  return { exportFile: parsed as CmsPagesExportFile, error: null };
}

export function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error ?? new Error("The file couldn't be read."));
    reader.readAsText(file);
  });
}
